import { marked } from 'marked';

// Custom marked extension to support image sizing syntax: ![alt](url =WIDTHxHEIGHT)
export const imageSizeExtension = {
  name: 'imageSize',
  level: 'inline' as const,
  start(src: string) {
    return src.match(/!\[/)?.index;
  },
  tokenizer(src: string) {
    // Match ![alt](url =WIDTHxHEIGHT) or ![alt](url =WIDTH) or ![alt](url =xHEIGHT)
    const rule = /^!\[([^\]]*)\]\(([^)]+)\s*=([0-9]*x?[0-9]*)\)/;
    const match = rule.exec(src);
    
    if (match) {
      const [fullMatch, alt, url, dimensions] = match;
      
      // Parse dimensions
      let width = '';
      let height = '';
      
      if (dimensions.includes('x')) {
        const [w, h] = dimensions.split('x');
        width = w || '';
        height = h || '';
      } else {
        // If no 'x', treat as width only
        width = dimensions;
      }
      
      return {
        type: 'imageSize',
        raw: fullMatch,
        alt: alt || '',
        href: url.trim(),
        width,
        height,
        dimensions
      };
    }
    
    return false;
  },
  renderer(token: any) {
    let style = '';
    const styles = [];
    
    if (token.width) {
      styles.push(`width: ${token.width}px`);
    }
    if (token.height) {
      styles.push(`height: ${token.height}px`);
    }
    
    if (styles.length > 0) {
      style = ` style="${styles.join('; ')}"`;
    }
    
    return `<img src="${token.href}" alt="${token.alt}"${style}>`;
  }
};

// Function to configure marked with the image size extension
export function configureMarkedWithImageSize() {
  marked.use({ extensions: [imageSizeExtension] });
}

// Function to parse image size syntax and convert to our internal format
export function parseImageSizeFromMarkdown(markdown: string): {
  url: string;
  alt: string;
  size: 'small' | 'medium' | 'large';
  customSize?: { width?: string; height?: string };
} | null {
  const rule = /!\[([^\]]*)\]\(([^)]+)\s*=([0-9]*x?[0-9]*)\)/;
  const match = rule.exec(markdown);
  
  if (match) {
    const [, alt, url, dimensions] = match;
    
    let width = '';
    let height = '';
    
    if (dimensions.includes('x')) {
      const [w, h] = dimensions.split('x');
      width = w || '';
      height = h || '';
    } else {
      width = dimensions;
    }
    
    // Determine size category based on width
    let size: 'small' | 'medium' | 'large' = 'medium';
    const widthNum = parseInt(width);
    if (widthNum && widthNum <= 300) {
      size = 'small';
    } else if (widthNum && widthNum >= 800) {
      size = 'large';
    }
    
    return {
      url: url.trim(),
      alt: alt || '',
      size,
      customSize: {
        width: width || undefined,
        height: height || undefined
      }
    };
  }
  
  return null;
}
